import React from 'react';
import { AlertCircle } from 'lucide-react';
import Combobox from './Combobox';
export { default as TagInput } from './TagInput';
export { default as BulletListInput } from './BulletListInput';

/**
 * Shared Form Components — Origin token-based styling.
 * Uses the .input / .textarea / .select / .field-label / .field-hint /
 * .field-error recipes from components.css.
 *
 * Back-compat: same prop API as before. Pages that pass icons keep the
 * leading-icon slot. Error / hint / disabled states preserved.
 */

function FieldShell({ label, required, hint, error, children }) {
  return (
    <div>
      {label && (
        <label className="field-label">
          {label}
          {required && <span style={{ color: 'var(--text-critical)', marginLeft: 2 }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && (
        <div className="field-error" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
}

// Chip-style single select: one click sets the value, clicking the selected
// chip clears it. Emits a synthetic change event so it drops into the same
// handleChange the selects use -- turns walls of "Select..." dropdowns into
// one-click answers (same pattern as the intake wizard's chips).
export function FormChipGroup({ label, name, value, onChange, options, required = false, hint }) {
  return (
    <FieldShell label={label} required={required} hint={hint}>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', paddingTop: 2 }}>
        {options.filter((option) => option.value !== '').map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`btn sm ${selected ? 'primary' : 'outline'}`}
              aria-pressed={selected}
              onClick={() => onChange({ target: { name, value: selected ? '' : option.value } })}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </FieldShell>
  );
}

// Form Input
export function FormInput({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  required = false,
  placeholder = '',
  hint,
  error,
  icon: Icon,
  disabled = false,
  className = '',
  ...props
}) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      <div style={{ position: 'relative' }}>
        {Icon && (
          <Icon
            className="w-4 h-4"
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--icon-secondary)',
              pointerEvents: 'none',
              // Date/time inputs are position:relative (for the native picker
              // overlay), which otherwise paints the opaque field over this
              // leading icon. Lift the icon above it.
              zIndex: 1,
            }}
          />
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          placeholder={placeholder}
          disabled={disabled}
          className={`input ${error ? 'danger' : ''} ${className}`}
          style={Icon ? { paddingLeft: 32 } : undefined}
          {...props}
        />
      </div>
    </FieldShell>
  );
}

// Form Combobox — a text input with an async/typeahead dropdown.
// Wraps Combobox in the same FieldShell used by FormInput / FormSelect so
// labels, hints, and errors line up consistently across the form.
export function FormCombobox({
  label,
  value,
  onChange,
  onSelect,
  options,
  placeholder,
  required = false,
  hint,
  error,
  disabled = false,
  emptyHint,
  maxVisible,
  name,
}) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      <Combobox
        value={value}
        onChange={onChange}
        onSelect={onSelect}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        emptyHint={emptyHint}
        maxVisible={maxVisible}
        inputProps={name ? { name } : undefined}
      />
    </FieldShell>
  );
}

// Form Select
export function FormSelect({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  hint,
  error,
  icon: Icon,
  disabled = false,
  className = '',
  ...props
}) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      <div style={{ position: 'relative' }}>
        {Icon && (
          <Icon
            className="w-4 h-4"
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--icon-secondary)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
        <select
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          className={`select ${error ? 'danger' : ''} ${className}`}
          style={Icon ? { paddingLeft: 32 } : undefined}
          {...props}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </FieldShell>
  );
}

// Form Textarea
export function FormTextarea({
  label,
  name,
  value,
  onChange,
  onBlur,
  required = false,
  placeholder = '',
  hint,
  error,
  rows = 4,
  disabled = false,
  className = '',
  ...props
}) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={`textarea ${error ? 'danger' : ''} ${className}`}
        {...props}
      />
    </FieldShell>
  );
}

// Form Section — card wrapper for form groups. Optional `description` renders a
// one-line subtitle under the title to orient the reader; when omitted the
// header is unchanged, so existing callers render identically.
export function FormSection({ title, description, icon: Icon, children, className = '' }) {
  return (
    <div className={`card ${className}`}>
      <div className="card-header" style={description ? { display: 'block' } : undefined}>
        <div className="row" style={{ gap: 8 }}>
          {Icon && <Icon className="w-4 h-4" style={{ color: 'var(--icon-secondary)', flexShrink: 0 }} />}
          <span className="card-title">{title}</span>
        </div>
        {description && (
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, lineHeight: 1.45 }}>
            {description}
          </div>
        )}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

// Search Input — filter bar style
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  icon: Icon,
  className = '',
  ...props
}) {
  return (
    <div style={{ position: 'relative' }} className={className}>
      {Icon && (
        <Icon
          className="w-4 h-4"
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--icon-tertiary)',
            pointerEvents: 'none',
          }}
        />
      )}
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input"
        style={Icon ? { paddingLeft: 32 } : undefined}
        {...props}
      />
    </div>
  );
}

// Filter Select
export function FilterSelect({
  value,
  onChange,
  options,
  icon: Icon,
  className = '',
  ...props
}) {
  return (
    <div style={{ position: 'relative' }} className={className}>
      {Icon && (
        <Icon
          className="w-4 h-4"
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--icon-tertiary)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
      <select
        value={value}
        onChange={onChange}
        className="filter-select"
        style={Icon ? { paddingLeft: 32 } : undefined}
        {...props}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

export function FilterSearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  icon: Icon,
  className = '',
  ...props
}) {
  return (
    <div className={`filter-search ${className}`.trim()}>
      {Icon && <Icon className="filter-search-icon" />}
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input filter-search-input"
        {...props}
      />
    </div>
  );
}

// Modal inputs — reuse main styling
export function ModalFormInput({ label, value, onChange, type = 'text', placeholder = '', optional = false, className = '', ...props }) {
  return (
    <div className={className}>
      <label className="field-label">
        {label}
        {optional && (
          <span style={{ color: 'var(--text-tertiary)', fontWeight: 450, marginLeft: 4 }}>(optional)</span>
        )}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input"
        {...props}
      />
    </div>
  );
}

export function ModalFormSelect({ label, value, onChange, options, className = '', ...props }) {
  return (
    <div className={className}>
      <label className="field-label">{label}</label>
      <select value={value} onChange={onChange} className="select" {...props}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.icon ? `${opt.icon} ${opt.label}` : opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function ModalFormTextarea({ label, value, onChange, placeholder = '', rows = 3, className = '', ...props }) {
  return (
    <div className={className}>
      <label className="field-label">{label}</label>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className="textarea"
        {...props}
      />
    </div>
  );
}
